import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TutorialButton } from "@/components/tutorials/TutorialButton";
import { api, formatErr, fmtDateTime } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Trash2, RefreshCw, X, Reply, CheckCircle2, Plus, CalendarClock, UserPlus, Users, Pencil, ShoppingCart, ArrowRight } from "lucide-react";
import { AddToShoppingDialog, ReplyShoppingSection, submitShoppingItems, TicketShoppingStrip } from "@/components/ShoppingList";
import { TicketsAiButton } from "@/components/TicketsAiButton";
import { CollapseToggle } from "@/components/TicketCollapse";
import { TicketSearchControl, Highlight } from "@/components/TicketSearch";
import { managementTicketResults } from "@/lib/managementTicketSearch";
import { TicketEditDialog } from "@/components/TicketEditDialog";
import PhotoAddButtons, { pasteToFiles } from "@/components/PhotoAddButtons";
import PushToggle from "@/components/PushToggle";
import { t as T } from "@/lib/i18n";
import { useAutoTranslate } from "@/lib/useAutoTranslate";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TicketSchedulesDialog } from "./TicketSchedulesDialog";
import { ColonyBadge, ColonyDotName } from "@/lib/colonyColors";
import { LazyPhotoStrip } from "@/components/PhotoLightbox";
// Photos are compressed in-browser before upload — no size rejections.
import { processMediaFiles } from "@/lib/livePhotoMedia";
import { LivePhotoSurface } from "@/components/LivePhotoSurface";
import { queueIfOffline } from "@/lib/offlineQueue";
import { useSelfHeight } from "@/lib/useSelfHeight";
import { useStickyBar } from "@/lib/useStickyBar";
import { HoverTime } from "@/components/HoverTime";

/**
 * Admin view of colony service-request tickets. Lives inside the
 * Management USA NJ Finance page. Filterable by status and by colony;
 * status updates in-place via PATCH /mgmt/colony-tickets/{tid}.
 */
const STATUS_ORDER = ["open", "in_progress", "hold", "done", "cancelled"];
// Filter pills additionally expose future-dated ("scheduled") tickets.
const FILTER_ORDER = [...STATUS_ORDER, "scheduled"];
const TICKET_PAGE_SIZE = 25;
const STATUS_META = {
  scheduled: { label: "Scheduled", cls: "bg-slate-100 text-slate-700 border-slate-300", triggerCls: "bg-background text-slate-700 border-slate-300", mobileTextCls: "text-slate-700" },
  open: { label: "Open", cls: "bg-amber-100 text-amber-800 border-amber-300", triggerCls: "bg-background text-amber-700 border-amber-300", mobileTextCls: "text-amber-700" },
  in_progress: { label: "In progress", cls: "bg-blue-100 text-blue-800 border-blue-300", triggerCls: "bg-background text-blue-700 border-blue-300", mobileTextCls: "text-blue-700" },
  hold: { label: "On hold", cls: "bg-purple-100 text-purple-800 border-purple-300", triggerCls: "bg-background text-purple-700 border-purple-300", mobileTextCls: "text-purple-700" },
  done: { label: "Closed", cls: "bg-emerald-100 text-emerald-800 border-emerald-300", triggerCls: "bg-background text-emerald-700 border-emerald-300", mobileTextCls: "text-emerald-700" },
  cancelled: { label: "Cancelled", cls: "bg-rose-50 text-slate-700 border-rose-300", triggerCls: "bg-background text-rose-700 border-rose-300", mobileTextCls: "text-rose-700" },
};

export default function ColonyTicketsAdmin({ focusTicketId = null, onGoToShoppingList, dateFrom = "", dateTo = "", setDateFrom = () => {}, setDateTo = () => {} } = {}) {
  const [filterBarRef, filterBarHeight] = useSelfHeight();
  const [sentinelRef, isStuck] = useStickyBar();
  const [tickets, setTickets] = useState([]);
  const [colonies, setColonies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [statusFilter, setStatusFilter] = useState(focusTicketId ? "all" : "open"); // open by default — most useful for triage
  const [colonyFilter, setColonyFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(null); // ticket id currently being mutated
  const [newOpen, setNewOpen] = useState(false);
  const [schedulesOpen, setSchedulesOpen] = useState(false);
  const [eligible, setEligible] = useState([]); // pool-side users who can be assigned

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, cRes, eRes] = await Promise.all([
        api.get("/mgmt/colony-tickets"),
        api.get("/mgmt/colonies"),
        api.get("/mgmt/eligible-watchers"),
      ]);
      setTickets(tRes.data || []);
      setColonies(cRes.data || []);
      setEligible(eRes.data || []);
      setHasLoaded(true);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("offline-synced", h);
    return () => window.removeEventListener("offline-synced", h);
  }, [load]);

  // Cross-links must reveal the same ticket regardless of its status.
  useEffect(() => {
    if (!focusTicketId) return;
    setStatusFilter("all");
    setColonyFilter("all");
    setSearch("");
  }, [focusTicketId]);

  // Scroll after the destination is loaded; its outline is React-owned below.
  useEffect(() => {
    if (!focusTicketId || loading) return;
    const timer = setTimeout(() => {
      const el = document.getElementById(`mgmt-ticket-${focusTicketId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [focusTicketId, loading, statusFilter, colonyFilter, search, dateFrom, dateTo]);

  const { filtered, counts, matches: searchMatches } = useMemo(() => {
    const cname = (id) => colonies.find((c) => c.id === id)?.name || "";
    const uname = (id) => { const u = eligible.find((x) => x.id === id); return u ? `${u.name || ""} ${u.email || ""}` : ""; };
    return managementTicketResults(tickets, {
      query: search, status: statusFilter, colony: colonyFilter,
      from: dateFrom, to: dateTo, sort: sortBy,
      colonyName: cname, assigneeName: uname, statusLabel: (s) => STATUS_META[s]?.label || "",
    });
  }, [tickets, statusFilter, colonyFilter, sortBy, colonies, eligible, search, dateFrom, dateTo]);

  const resultKey = `${statusFilter}|${colonyFilter}|${sortBy}|${search.trim()}|${dateFrom}|${dateTo}`;
  const [paging, setPaging] = useState({ key: "", limit: TICKET_PAGE_SIZE });
  useEffect(() => {
    setPaging({ key: resultKey, limit: TICKET_PAGE_SIZE });
  }, [resultKey]);
  const pageLimit = paging.key === resultKey ? paging.limit : TICKET_PAGE_SIZE;
  const focusedIndex = focusTicketId ? filtered.findIndex((ticket) => ticket.id === focusTicketId) : -1;
  const visibleLimit = Math.max(pageLimit, focusedIndex + 1);
  const visibleTickets = useMemo(() => filtered.slice(0, visibleLimit), [filtered, visibleLimit]);

  // Auto-translate ticket content when the UI language is Spanish.
  const dynTexts = useMemo(
    () => visibleTickets.flatMap((tk) => [tk.title, tk.description, ...(tk.notes || []).map((n) => n.body)]),
    [visibleTickets]
  );
  const atr = useAutoTranslate(api, dynTexts);

  const changeStatus = async (t, next) => {
    if (t.status === next) return;
    setBusy(t.id);
    try {
      await api.patch(`/mgmt/colony-tickets/${t.id}`, { status: next });
      // Optimistic local update so the ticket doesn't jump around while
      // the network round-trips — matches admin's mental model.
      setTickets((prev) => prev.map((x) => (x.id === t.id ? { ...x, status: next } : x)));
      toast.success(`Marked ${STATUS_META[next]?.label || next}`);
    } catch (e) {
      if (queueIfOffline(e, { portal: "api", method: "patch", path: `/mgmt/colony-tickets/${t.id}`, body: { status: next }, label: `Mark ticket "${t.title}" ${STATUS_META[next]?.label || next}` })) {
        setTickets((prev) => prev.map((x) => (x.id === t.id ? { ...x, status: next } : x)));
      } else {
        toast.error(formatErr(e.response?.data?.detail) || e.message);
      }
    } finally {
      setBusy(null);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.title}"? This can't be undone.`)) return;
    setBusy(t.id);
    try {
      await api.delete(`/mgmt/colony-tickets/${t.id}`);
      setTickets((prev) => prev.filter((x) => x.id !== t.id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const colonyName = (id) => colonies.find((c) => c.id === id)?.name || "—";

  return (
    <div className="space-y-4" data-testid="mgmt-colony-tickets">
      {/* Header + refresh */}
      <div className="management-section-header flex items-center justify-between gap-3 flex-wrap mobile-stack">
        <div className="min-w-0 max-w-full">
        <div className="flex flex-wrap items-center gap-3"><h1 className="management-title font-display" data-testid="management-tickets-heading">Tickets</h1><TutorialButton section="tickets" compact /></div>
        <p className="text-sm text-muted-foreground mt-1 [overflow-wrap:anywhere]" data-testid="management-tickets-summary">
          {filtered.length} of {tickets.length} tickets
          {statusFilter !== "all" && ` · showing ${STATUS_META[statusFilter]?.label || statusFilter}`}
          {colonyFilter !== "all" && ` · ${colonyName(colonyFilter)}`}
          {search.trim() && ` · matching "${search.trim()}" · title matches first`}
          {(dateFrom || dateTo) && ` · ${dateFrom || "…"} → ${dateTo || "…"}`}
          {visibleTickets.length < filtered.length && ` · ${visibleTickets.length} loaded`}
        </p>
        </div>
        <div className="grid grid-cols-2 sm:flex gap-2 mobile-full">
          <PushToggle client={api} base="/push" targetUrl="/managementusa/admin" testidPrefix="mgmt-push" />
          <Button
            size="sm"
            className="rounded-sm px-2 sm:px-3"
            onClick={() => setNewOpen(true)}
            data-testid="mgmt-tickets-new"
          >
            <Plus className="w-3.5 h-3.5 mr-2" /> New ticket
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm px-2 sm:px-3"
            onClick={() => setSchedulesOpen(true)}
            data-testid="mgmt-tickets-schedules"
          >
            <CalendarClock className="w-3.5 h-3.5 mr-2" /> Auto tickets
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm px-2 sm:px-3"
            onClick={load}
            disabled={loading}
            data-testid="mgmt-tickets-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Filter row — compact search icon + pill-style status filter + colony dropdown.
          Renders in normal flow until scrolled up to the header, then
          switches to real `position: fixed` (a sentinel that mimics
          `sticky` via IntersectionObserver — plain CSS `sticky` here would
          hit a long-standing iOS Safari WebKit bug with two stacked/nested
          sticky elements, this bar + the header above, where the second
          one stops tracking scroll). Only fixed while actually stuck, so
          it never overlaps the content that sits above it before that. */}
      <div ref={sentinelRef} />
      <div
        ref={filterBarRef}
        data-testid="mgmt-ticket-filter-bar"
        className={isStuck
          ? "fixed inset-x-0 z-20 max-w-7xl mx-auto !mt-0 px-3 sm:px-4 py-2 bg-background/95 backdrop-blur-sm"
          : "relative z-20 -mx-3 px-3 sm:-mx-4 sm:px-4 py-2 bg-background/95 backdrop-blur-sm"}
        style={isStuck ? { top: "var(--sticky-header-h, 0px)" } : undefined}
      >
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3 items-end">
        <div className="hidden sm:block">
          <TicketSearchControl
            search={search} setSearch={setSearch}
            dateFrom={dateFrom} setDateFrom={setDateFrom}
            dateTo={dateTo} setDateTo={setDateTo}
            testidPrefix="mgmt-tickets"
            fullWidthWhenOpen
            collapseWhenSearchEmpty
            preserveDatesOnClose
          />
        </div>
        <div className="col-span-2 min-w-0">
            <div className="sm:hidden min-w-0 mb-2">
            <TicketSearchControl
              search={search} setSearch={setSearch}
              dateFrom={dateFrom} setDateFrom={setDateFrom}
              dateTo={dateTo} setDateTo={setDateTo}
              testidPrefix="mgmt-tickets-mobile"
              fullWidthWhenOpen
              collapseWhenSearchEmpty
              preserveDatesOnClose
              responsive
            />
            </div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1" data-testid="mgmt-ticket-status-label">Status</div>
            <div className="flex flex-wrap gap-1 pb-1 min-w-0">
              <FilterPill
                label={`${T("All")} (${counts.all || 0})`}
                active={statusFilter === "all"}
                onClick={() => setStatusFilter("all")}
                testid="mgmt-status-filter-all"
              />
              {FILTER_ORDER.map((s) => (
                <FilterPill
                  key={s}
                  label={`${T(STATUS_META[s].label)} (${counts[s] || 0})`}
                  active={statusFilter === s}
                  onClick={() => setStatusFilter(s)}
                  pillCls={STATUS_META[s].cls}
                  testid={`mgmt-status-filter-${s}`}
                />
              ))}
            </div>
        </div>
        <div className="min-w-0 sm:min-w-[220px]">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Colony</div>
          <Select value={colonyFilter} onValueChange={setColonyFilter}>
            <SelectTrigger className="h-11 sm:h-9 rounded-sm" data-testid="mgmt-colony-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="mgmt-colony-option-all">All colonies</SelectItem>
              {colonies.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`mgmt-colony-option-${c.id}`}><ColonyDotName id={c.id} name={c.name} /></SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-0 sm:min-w-[190px]">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Sort by</div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-11 sm:h-9 rounded-sm" data-testid="mgmt-sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest" data-testid="mgmt-sort-option-newest">Newest first</SelectItem>
              <SelectItem value="oldest" data-testid="mgmt-sort-option-oldest">Oldest first</SelectItem>
              <SelectItem value="updated" data-testid="mgmt-sort-option-updated">Last modified or closed</SelectItem>
              <SelectItem value="colony" data-testid="mgmt-sort-option-colony">Bungalow colony</SelectItem>
              <SelectItem value="scheduled" data-testid="mgmt-sort-option-scheduled">Scheduled date</SelectItem>
            </SelectContent>
          </Select>
        </div>
        </div>
      </div>
      {isStuck && <div style={{ height: filterBarHeight }} aria-hidden="true" />}

      {/* Ticket list */}
      {focusTicketId && !loading && hasLoaded && !tickets.some(ticket => ticket.id === focusTicketId) && (
        <div className="border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950" role="status" data-testid="mgmt-linked-ticket-unavailable">
          This linked ticket is no longer available.
          {onGoToShoppingList && <Button variant="ghost" size="sm" onClick={onGoToShoppingList} data-testid="mgmt-linked-ticket-back">Back to shopping list</Button>}
        </div>
      )}
      {loading ? (
        <div className="text-sm text-muted-foreground py-6">Loading tickets…</div>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center border-dashed" data-testid="mgmt-tickets-empty">
          <h3 className="font-display text-lg font-bold">Nothing here</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {tickets.length === 0
              ? "No colony tickets yet. When a colony contact files a request from their portal it'll show up here."
              : "No tickets match the current filters — try clearing them."}
          </p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="mgmt-tickets-list">
          {visibleTickets.map((t) => (
            <TicketRow
              key={t.id}
              ticket={t}
              highlight={search}
              searchContext={searchMatches.get(t.id)?.context}
              atr={atr}
              colonyName={colonyName(t.colony_id)}
              busy={busy === t.id}
              eligible={eligible}
              onChangeStatus={(s) => changeStatus(t, s)}
              onDelete={() => remove(t)}
              onTicketUpdated={(updated) => setTickets((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))}
              onGoToShoppingList={onGoToShoppingList}
              isFocused={t.id === focusTicketId}
            />
          ))}
          {visibleTickets.length < filtered.length && (
            <div className="flex justify-center pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPaging({ key: resultKey, limit: visibleLimit + TICKET_PAGE_SIZE })}
                className="rounded-sm"
                data-testid="mgmt-tickets-load-more"
              >
                Load 25 more · {filtered.length - visibleTickets.length} remaining
              </Button>
            </div>
          )}
        </div>
      )}

      <AdminNewTicketDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        colonies={colonies}
        eligible={eligible}
        defaultColonyId={colonyFilter !== "all" ? colonyFilter : ""}
        onCreated={(t) => { setNewOpen(false); setTickets((prev) => [t, ...prev]); toast.success("Ticket created."); }}
      />
      <TicketSchedulesDialog
        open={schedulesOpen}
        onOpenChange={setSchedulesOpen}
        colonies={colonies}
        eligible={eligible}
      />
      <TicketsAiButton client={api} askPath="/mgmt/colony-tickets/ask" />
    </div>
  );
}

function FilterPill({ label, active, onClick, pillCls = "", testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 min-h-9 sm:min-h-0 text-xs font-semibold rounded-sm border transition-colors whitespace-nowrap ${
        active
          ? `${pillCls || "bg-primary text-primary-foreground border-primary"} shadow-sm`
          : "border-border text-muted-foreground hover:text-foreground hover:border-foreground"
      }`}
      data-testid={testid}
    >
      {label}
    </button>
  );
}

function TicketRow({ ticket, colonyName, busy, eligible = [], highlight = "", searchContext = null, atr = (x) => x, onChangeStatus, onDelete, onTicketUpdated, onGoToShoppingList, isFocused = false }) {
  const meta = STATUS_META[ticket.status] || STATUS_META.open;
  const notes = ticket.notes || [];
  const [replyOpen, setReplyOpen] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(ticket.status === "in_progress" || isFocused);
  const [threadOpen, setThreadOpen] = useState(ticket.status === "in_progress" || isFocused);
  useEffect(() => {
    if (isFocused) { setDetailsOpen(true); setThreadOpen(true); }
  }, [isFocused]);

  // Closing a ticket first offers to attach photos of the completed
  // work — with a "Skip & close" escape hatch.
  const handleStatusChange = (next) => {
    if (next === "done" && ticket.status !== "done") {
      setCloseOpen(true);
      return;
    }
    onChangeStatus(next);
  };

  const currentAssignees = ticket.assignees || [];
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const saveEdit = async (p) => {
    setEditSaving(true);
    try {
      const { data } = await api.patch(`/mgmt/colony-tickets/${ticket.id}`, p);
      onTicketUpdated(data);
      toast.success("Ticket updated.");
      setEditOpen(false);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setEditSaving(false);
    }
  };
  const assigneeLabel = (uid) => {
    const u = eligible.find((x) => x.id === uid);
    return u ? (u.name || u.email) : uid.slice(0, 6);
  };
  const setAssignees = async (nextIds) => {
    setAssignSaving(true);
    try {
      const { data } = await api.patch(`/mgmt/colony-tickets/${ticket.id}`, { assignees: nextIds });
      onTicketUpdated(data);
      toast.success(nextIds.length ? "Assignees updated" : "Cleared assignees");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setAssignSaving(false);
    }
  };
  const toggleAssignee = (uid) => {
    const next = currentAssignees.includes(uid)
      ? currentAssignees.filter((x) => x !== uid)
      : [...currentAssignees, uid];
    setAssignees(next);
  };

  return (
    <div
      className={`management-ticket-card group border rounded-sm px-3 py-2 sm:px-4 sm:py-3 transition-[border-color,background-color,box-shadow] ${ticket.urgent && (ticket.status === "open" || ticket.status === "in_progress") ? "border-red-300 bg-red-50/70" : "border-border bg-background hover:border-foreground/20"}`}
      id={`mgmt-ticket-${ticket.id}`}
      data-focused={isFocused ? "true" : "false"}
      style={isFocused ? { outline: "3px solid hsl(var(--primary))", outlineOffset: "2px" } : undefined}
      data-testid={`mgmt-ticket-${ticket.id}`}
    >
      <div className="flex items-start justify-between gap-2 sm:gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-base [overflow-wrap:anywhere]" data-testid={`mgmt-ticket-title-${ticket.id}`}><Highlight text={atr(ticket.title)} query={highlight} /></h3>
            {ticket.urgent && (
              <span
                className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-red-400 bg-red-100 text-red-800 font-bold"
                data-testid={`mgmt-ticket-urgent-${ticket.id}`}
              >
                Urgent
              </span>
            )}
            <span
              className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${meta.cls}`}
              data-testid={`mgmt-ticket-status-${ticket.id}`}
            >
              {T(meta.label)}
            </span>
            {ticket.scheduled_for && (
              <span
                className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-purple-300 bg-purple-50 text-purple-800 inline-flex items-center gap-1"
                title={ticket.status === "scheduled" ? "Will be filed automatically at this date & time" : "Revisit on this date"}
                data-testid={`mgmt-ticket-scheduled-${ticket.id}`}
              >
                <CalendarClock className="w-3 h-3" /> {fmtDateTime(ticket.scheduled_for)}
              </span>
            )}
            {notes.length > 0 && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-border text-muted-foreground">
                {notes.length} {notes.length === 1 ? "reply" : "replies"}
              </span>
            )}
          </div>
          {searchContext && <p className="mt-1 text-xs text-muted-foreground [overflow-wrap:anywhere]" data-testid={`mgmt-ticket-match-${ticket.id}`}>
            <span className="font-medium" data-testid={`mgmt-ticket-match-label-${ticket.id}`}>{searchContext.label}: </span>
            <Highlight text={searchContext.text} query={highlight} />
          </p>}
          <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
            <ColonyBadge id={ticket.colony_id} name={colonyName} />
            <span>{"Filed "}<HoverTime iso={ticket.created_at} label="Filed " testId={`mgmt-ticket-filed-${ticket.id}`} />{" · by "}<Highlight text={ticket.created_by_name || ticket.created_by_email} query={highlight} /></span>
          </div>
          <CollapseToggle
            open={detailsOpen}
            onClick={() => setDetailsOpen((v) => !v)}
            label={detailsOpen ? "Hide details" : "Show details"}
            testid={`mgmt-ticket-details-toggle-${ticket.id}`}
            className="mt-2"
          />
          {detailsOpen && ticket.description && (
            <p className="text-sm mt-2 whitespace-pre-wrap"><Highlight text={atr(ticket.description)} query={highlight} /></p>
          )}
          {/* Photos + shopping items always visible — never hidden behind the Details toggle. */}
          <div className="sm:mt-2 sm:flex sm:flex-wrap sm:items-end sm:gap-2" data-testid={`mgmt-ticket-media-actions-${ticket.id}`}>
          <Button
            variant="outline"
            size="sm"
            className="hidden sm:inline-flex rounded-sm h-8 justify-start"
            onClick={() => setReplyOpen((v) => !v)}
            data-testid={`mgmt-ticket-reply-toggle-${ticket.id}`}
          >
            <Reply className="w-3.5 h-3.5 mr-2" /> {replyOpen ? "Hide reply" : "Add reply"}
          </Button>
          {(ticket.photos?.length || ticket.photo_count) ? (
            <div className="mt-3 min-w-0 max-w-full sm:mt-0">
              <LazyPhotoStrip
                photos={ticket.photos}
                count={ticket.photo_count}
                fetchPhotos={() => api.get(`/mgmt/colony-tickets/${ticket.id}/photos`).then((r) => r.data.photos)}
                alt="ticket photo"
                buttonClassName="sm:h-8 sm:py-0"
                testid={`mgmt-ticket-photo-${ticket.id}`}
                cacheKey={`ticket-${ticket.id}`}
              />
            </div>
          ) : null}
          <TicketShoppingStrip
            inlineActions
            count={ticket.shopping_count}
            fetchItems={() => api.get(`/mgmt/colony-tickets/${ticket.id}/shopping`).then((r) => r.data)}
            client={api}
            basePath="/mgmt"
            T={T}
            testid={`mgmt-ticket-shopping-items-${ticket.id}`}
          />
          </div>
        </div>
        <div className="-mt-3 pr-2 grid sm:hidden grid-cols-[auto_minmax(0,1fr)_120px] gap-x-1.5 gap-y-1 w-full items-center">
          <div
            className="col-start-1 row-start-1 justify-start flex items-center gap-0.5 sm:gap-1 opacity-100 sm:opacity-0 sm:pointer-events-none sm:group-hover:opacity-100 sm:group-hover:pointer-events-auto sm:group-focus-within:opacity-100 sm:group-focus-within:pointer-events-auto transition-opacity duration-150"
            data-testid={`mgmt-mobile-ticket-hover-actions-${ticket.id}`}
          >
            <Button
              variant="ghost"
              size="icon"
              className="tap-exempt rounded-sm h-7 w-7 sm:h-9 sm:w-9 text-muted-foreground hover:text-foreground"
              onClick={() => setEditOpen(true)}
              title="Edit ticket"
              aria-label={`Edit ${ticket.title}`}
              data-testid={`mgmt-mobile-ticket-edit-${ticket.id}`}
            >
              <Pencil className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="tap-exempt rounded-sm h-7 w-7 sm:h-9 sm:w-9 text-muted-foreground hover:text-foreground"
              onClick={() => setShopOpen(true)}
              title="Add to shopping list"
              aria-label={`Add ${ticket.title} to shopping list`}
              data-testid={`mgmt-mobile-ticket-shopping-${ticket.id}`}
            >
              <ShoppingCart className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </Button>
            {onGoToShoppingList && (
              <Button
                variant="ghost"
                size="icon"
                className="tap-exempt rounded-sm h-7 w-7 sm:h-9 sm:w-9 text-muted-foreground hover:text-foreground"
                onClick={onGoToShoppingList}
                title="Go to shopping list"
                aria-label="Go to shopping list"
                data-testid={`mgmt-mobile-ticket-shopping-goto-${ticket.id}`}
              >
                <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="tap-exempt rounded-sm h-7 w-7 sm:h-9 sm:w-9 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
              onClick={onDelete}
              disabled={busy}
              title="Delete ticket"
              aria-label={`Delete ${ticket.title}`}
              data-testid={`mgmt-mobile-ticket-delete-${ticket.id}`}
            >
              <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </Button>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="tap-exempt col-start-1 row-start-2 rounded-sm h-7 sm:h-8 w-auto justify-self-start justify-start px-2.5 sm:px-3 text-xs sm:text-sm sm:font-normal border-transparent bg-transparent shadow-none hover:bg-secondary/40 sm:border-input sm:bg-background"
            onClick={() => setReplyOpen((v) => !v)}
            data-testid={`mgmt-mobile-ticket-reply-toggle-${ticket.id}`}
          >
            <Reply className="w-3.5 h-3.5 mr-2" /> {replyOpen ? "Hide reply" : "Add reply"}
          </Button>
          <div className="col-start-3 row-start-1 row-span-2 -mt-5 flex flex-col gap-1 sm:contents sm:mt-0">
          <Select value={ticket.status} onValueChange={handleStatusChange} disabled={busy}>
            <SelectTrigger className={`tap-exempt h-7 w-full rounded-sm px-2 text-xs !border-transparent !bg-transparent shadow-none ${meta.mobileTextCls}`} data-testid={`mgmt-mobile-ticket-set-status-${ticket.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ticket.status === "scheduled" && (
                  <SelectItem value="scheduled" disabled className={`border ${STATUS_META.scheduled.triggerCls} my-1`}>Scheduled — pick Open to activate now</SelectItem>
              )}
              {STATUS_ORDER.map((s) => (
                <SelectItem key={s} value={s} className={`border ${STATUS_META[s].triggerCls} my-1`}>{T(STATUS_META[s].label)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="tap-exempt sm:col-start-3 sm:row-start-2 rounded-sm h-7 sm:h-8 w-full justify-start px-2 sm:px-3 text-[11px] sm:text-xs border-transparent bg-transparent shadow-none hover:bg-secondary/40 sm:border-input sm:bg-background"
                disabled={assignSaving}
                data-testid={`mgmt-mobile-ticket-assign-toggle-${ticket.id}`}
              >
                <UserPlus className="w-3.5 h-3.5 mr-1.5 shrink-0" />
                <span className="truncate" title={currentAssignees.length > 0 ? currentAssignees.map(assigneeLabel).join(", ") : "Assign worker"}>
                  {currentAssignees.length > 0
                    ? `Assigned (${currentAssignees.length})`
                    : "Assign worker"}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0" align="end">
              <div className="px-3 py-2 border-b border-border flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" /> Assign to
                </div>
                {currentAssignees.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setAssignees([])}
                    className="text-[11px] text-muted-foreground hover:text-foreground underline"
                    data-testid={`mgmt-mobile-ticket-assign-clear-${ticket.id}`}
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="max-h-64 overflow-auto py-1" data-testid={`mgmt-mobile-ticket-assign-list-${ticket.id}`}>
                {eligible.length === 0 ? (
                  <div className="px-3 py-4 text-xs text-muted-foreground">
                    No workers yet. Create worker logins in Manage workers → Key icon.
                  </div>
                ) : (
                  eligible.map((u) => {
                    const checked = currentAssignees.includes(u.id);
                    return (
                      <label
                        key={u.id}
                        className="flex items-center gap-2 px-3 py-1.5 hover:bg-secondary/50 cursor-pointer text-sm"
                        data-testid={`mgmt-mobile-ticket-assign-option-${ticket.id}-${u.id}`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleAssignee(u.id)}
                          disabled={assignSaving}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="truncate">{u.name || u.email}</div>
                          {u.name && (
                            <div className="text-[11px] text-muted-foreground truncate">{u.email}</div>
                          )}
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            </PopoverContent>
          </Popover>
          <div className="min-h-4 flex items-center justify-end overflow-hidden" data-testid={`mgmt-mobile-ticket-assignees-${ticket.id}`}>
            {currentAssignees.length > 0 && (
              <div className="flex flex-nowrap justify-end gap-1 min-w-0 overflow-hidden">
                {currentAssignees.map((uid) => (
                  <span
                    key={uid}
                    className="max-w-[150px] truncate text-[9px] sm:text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-emerald-300 bg-emerald-50 text-emerald-800"
                  >
                    {assigneeLabel(uid)}
                  </span>
                ))}
              </div>
            )}
          </div>
          </div>
        </div>
        <div className="hidden sm:grid grid-cols-[auto_150px] gap-x-2 gap-y-2 w-auto ml-auto self-start">
          <div
            className="col-start-1 row-start-1 flex items-center justify-end gap-1 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto transition-opacity duration-150"
            data-testid={`mgmt-ticket-hover-actions-${ticket.id}`}
          >
            <Button variant="ghost" size="icon" className="rounded-sm h-9 w-9 text-muted-foreground hover:text-foreground" onClick={() => setEditOpen(true)} title="Edit ticket" aria-label={`Edit ${ticket.title}`} data-testid={`mgmt-ticket-edit-${ticket.id}`}>
              <Pencil className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" className="rounded-sm h-9 w-9 text-muted-foreground hover:text-foreground" onClick={() => setShopOpen(true)} title="Add to shopping list" aria-label={`Add ${ticket.title} to shopping list`} data-testid={`mgmt-ticket-shopping-${ticket.id}`}>
              <ShoppingCart className="w-4 h-4" />
            </Button>
            {onGoToShoppingList && (
              <Button variant="ghost" size="icon" className="rounded-sm h-9 w-9 text-muted-foreground hover:text-foreground" onClick={onGoToShoppingList} title="Go to shopping list" aria-label="Go to shopping list" data-testid={`mgmt-ticket-shopping-goto-${ticket.id}`}>
                <ArrowRight className="w-4 h-4" />
              </Button>
            )}
            <Button variant="ghost" size="icon" className="rounded-sm h-9 w-9 text-muted-foreground hover:text-destructive hover:bg-destructive/10" onClick={onDelete} disabled={busy} title="Delete ticket" aria-label={`Delete ${ticket.title}`} data-testid={`mgmt-ticket-delete-${ticket.id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
          <Select value={ticket.status} onValueChange={handleStatusChange} disabled={busy}>
            <SelectTrigger className={`col-start-2 row-start-1 h-9 w-[150px] rounded-sm ${meta.triggerCls}`} data-testid={`mgmt-ticket-set-status-${ticket.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ticket.status === "scheduled" && (
                <SelectItem value="scheduled" disabled className={`border ${STATUS_META.scheduled.triggerCls} my-1`}>Scheduled — pick Open to activate now</SelectItem>
              )}
              {STATUS_ORDER.map((s) => (
                <SelectItem key={s} value={s} className={`border ${STATUS_META[s].triggerCls} my-1`}>{T(STATUS_META[s].label)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="col-start-2 row-start-2 rounded-sm h-8 w-[150px] justify-start"
                disabled={assignSaving}
                data-testid={`mgmt-ticket-assign-toggle-${ticket.id}`}
              >
                <UserPlus className="w-3.5 h-3.5 mr-2" />
                {currentAssignees.length > 0
                  ? `Assigned (${currentAssignees.length})`
                  : "Assign worker"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0" align="end">
              <div className="px-3 py-2 border-b border-border flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" /> Assign to
                </div>
                {currentAssignees.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setAssignees([])}
                    className="text-[11px] text-muted-foreground hover:text-foreground underline"
                    data-testid={`mgmt-ticket-assign-clear-${ticket.id}`}
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="max-h-64 overflow-auto py-1" data-testid={`mgmt-ticket-assign-list-${ticket.id}`}>
                {eligible.length === 0 ? (
                  <div className="px-3 py-4 text-xs text-muted-foreground">
                    No workers yet. Create worker logins in Manage workers → Key icon.
                  </div>
                ) : (
                  eligible.map((u) => {
                    const checked = currentAssignees.includes(u.id);
                    return (
                      <label
                        key={u.id}
                        className="flex items-center gap-2 px-3 py-1.5 hover:bg-secondary/50 cursor-pointer text-sm"
                        data-testid={`mgmt-ticket-assign-option-${ticket.id}-${u.id}`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleAssignee(u.id)}
                          disabled={assignSaving}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="truncate">{u.name || u.email}</div>
                          {u.name && (
                            <div className="text-[11px] text-muted-foreground truncate">{u.email}</div>
                          )}
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            </PopoverContent>
          </Popover>
          {currentAssignees.length > 0 && (
            <div className="col-start-2 row-start-3 w-[150px] flex justify-end min-w-0" data-testid={`mgmt-ticket-assignees-${ticket.id}`}>
              <div className="flex flex-nowrap justify-end gap-1 min-w-0 overflow-hidden">
                {currentAssignees.map((uid) => (
                  <span key={uid} className="max-w-[150px] truncate text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-emerald-300 bg-emerald-50 text-emerald-800" data-testid={`mgmt-ticket-assignee-label-${ticket.id}-${uid}`}>
                    {assigneeLabel(uid)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Existing notes thread */}
      {notes.length > 0 && (
        <div className="mt-4 border-t border-border pt-3 space-y-3" data-testid={`mgmt-ticket-notes-${ticket.id}`}>
          <CollapseToggle
            open={threadOpen}
            onClick={() => setThreadOpen((v) => !v)}
            label={`Replies (${notes.length})`}
            testid={`mgmt-ticket-thread-toggle-${ticket.id}`}
          />
          {threadOpen && notes.map((n) => (
            <NoteBubble key={n.id} ticketId={ticket.id} note={n} highlight={highlight} atr={atr} onDeleted={onTicketUpdated} />
          ))}
        </div>
      )}

      {/* Reply form */}
      {replyOpen && (
        <ReplyForm
          ticketId={ticket.id}
            colonyId={ticket.colony_id}
          canCloseAtSame={ticket.status !== "done" && ticket.status !== "cancelled"}
          onDone={(updated) => { setReplyOpen(false); onTicketUpdated(updated); }}
        />
      )}

      <CloseTicketDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        ticket={ticket}
        onSkip={() => { setCloseOpen(false); onChangeStatus("done"); }}
        onClosed={(updated) => { setCloseOpen(false); onTicketUpdated(updated); }}
      />
      <TicketEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        ticket={ticket}
        saving={editSaving}
        onSave={saveEdit}
      />
      <AddToShoppingDialog
        open={shopOpen}
        onOpenChange={setShopOpen}
        client={api}
        path={`/mgmt/colony-tickets/${ticket.id}/shopping`}
        ticketTitle={ticket.title}
      />
    </div>
  );
}

function NoteBubble({ ticketId, note, onDeleted, highlight = "", atr = (x) => x }) {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    if (!window.confirm("Delete this reply?")) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/mgmt/colony-tickets/${ticketId}/notes/${note.id}`);
      onDeleted(data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-secondary/40 border border-border rounded-sm p-3" data-testid={`mgmt-ticket-note-${note.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-muted-foreground">
          <strong className="text-foreground">{note.author_name || note.author_email || "Office"}</strong>
          {" · "}<HoverTime iso={note.created_at} testId={`mgmt-ticket-note-time-${note.id}`} />
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
          onClick={remove}
          disabled={busy}
          data-testid={`mgmt-ticket-note-delete-${note.id}`}
          title="Delete this reply"
        >
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>
      {note.body && <p className="text-sm mt-1 whitespace-pre-wrap"><Highlight text={atr(note.body)} query={highlight} /></p>}
      {(note.photos?.length || note.photo_count) ? (
        <div className="mt-2">
          <LazyPhotoStrip
            photos={note.photos}
            count={note.photo_count}
            fetchPhotos={() => api.get(`/mgmt/colony-tickets/${ticketId}/photos`).then((r) => (r.data.note_photos || {})[note.id] || [])}
            alt="reply photo"
            size="w-16 h-16"
            cacheKey={`note-${note.id}`}
          />
        </div>
      ) : null}
    </div>
  );
}

function ReplyForm({ ticketId, colonyId, canCloseAtSame, onDone }) {
  const [body, setBody] = useState("");
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [shopItems, setShopItems] = useState([]);

  const addFiles = async (files) => {
    if (!files) return;
    try {
      const result = await processMediaFiles(files, { max: 6, existingCount: photos.length, client: api, scope: "mgmt_usa_nj", colonyId });
      setPhotos((current) => [...current, ...result.items].slice(0, 6));
      if (result.ignoredVideos) toast.message("Select the MOV together with its matching iPhone photo.");
    } catch (error) { toast.error(error.message || "Could not add that photo."); }
  };

  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  const submit = async (closeToo) => {
    const text = body.trim();
    if (!text && photos.length === 0) {
      return toast.error("Add a note, a photo, or both.");
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/mgmt/colony-tickets/${ticketId}/notes`, {
        body: text,
        photos,
        close_ticket: !!closeToo,
      });
      const nAdded = await submitShoppingItems(api, `/mgmt/colony-tickets/${ticketId}/shopping`, shopItems);
      toast.success(closeToo ? "Reply sent and ticket closed." : "Reply added.");
      if (nAdded) toast.success(`${nAdded} ${nAdded === 1 ? "product" : "products"} added to shopping list.`);
      onDone(data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 border-t border-border pt-3 space-y-2" data-testid={`mgmt-ticket-reply-form-${ticketId}`}>
      <Textarea
        placeholder="Write a note back to the colony…  (you can also paste a picture here)"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onPaste={pasteToFiles(addFiles)}
        rows={3}
        className="rounded-sm"
        data-testid={`mgmt-ticket-reply-body-${ticketId}`}
      />

      <div className="flex flex-wrap gap-2 items-center">
        {photos.map((p, i) => (
          <div key={i} className="relative">
            <LivePhotoSurface value={p} alt={`upload ${i + 1}`} className="w-14 h-14 rounded-sm border border-border" testId={`mgmt-ticket-reply-preview-${ticketId}-${i}`} />
            <button
              type="button"
              onClick={() => removePhoto(i)}
              className="absolute -top-1 -right-1 bg-background border border-border rounded-full w-5 h-5 flex items-center justify-center tap-exempt"
              aria-label="Remove photo"
              data-testid={`mgmt-ticket-reply-photo-remove-${ticketId}-${i}`}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {photos.length < 6 && (
          <PhotoAddButtons onFiles={addFiles} testidPrefix={`mgmt-ticket-reply-${ticketId}`} />
        )}
      </div>

      <ReplyShoppingSection items={shopItems} setItems={setShopItems} T={T} />

      <div className="flex flex-wrap gap-2 justify-end">
        <Button
          variant="outline"
          size="sm"
          className="rounded-sm"
          onClick={() => submit(false)}
          disabled={saving}
          data-testid={`mgmt-ticket-reply-send-${ticketId}`}
        >
          <Reply className="w-3.5 h-3.5 mr-2" /> {saving ? "Sending…" : "Send reply"}
        </Button>
        {canCloseAtSame && (
          <Button
            size="sm"
            className="rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white"
            onClick={() => submit(true)}
            disabled={saving}
            data-testid={`mgmt-ticket-reply-send-close-${ticketId}`}
          >
            <CheckCircle2 className="w-3.5 h-3.5 mr-2" /> {saving ? "Sending…" : "Send and close"}
          </Button>
        )}
      </div>
    </div>
  );
}


/**
 * Shown when the admin flips a ticket's status to Closed. Offers to
 * attach photos of the finished work (+ optional note) — or skip and
 * just close. "Close with photos" posts a note with close_ticket=true
 * so the photos land on the ticket thread and it closes in one call.
 */
function CloseTicketDialog({ open, onOpenChange, ticket, onSkip, onClosed }) {
  const [photos, setPhotos] = useState([]);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (open) { setPhotos([]); setNote(""); }
  }, [open]);

  const addFiles = async (files) => {
    if (!files) return;
    try {
      const result = await processMediaFiles(files, { max: 6, existingCount: photos.length, client: api, scope: "mgmt_usa_nj", colonyId: ticket?.colony_id });
      setPhotos((current) => [...current, ...result.items].slice(0, 6));
      if (result.ignoredVideos) toast.message("Select the MOV together with its matching iPhone photo.");
    } catch (error) { toast.error(error.message || "Could not add that photo."); }
  };

  const closeWithPhotos = () => {
    if (!photos.length && !note.trim()) {
      return toast.error("Add a photo or note — or use Skip & close.");
    }
    // Optimistic close: the UI updates instantly, photos upload in the
    // background. On failure the ticket is restored and the admin told.
    const payload = { body: note.trim(), photos, close_ticket: true };
    onClosed({ ...ticket, status: "done" });
    toast.success(photos.length ? "Ticket closed — photos uploading in the background…" : "Ticket closed.");
    api.post(`/mgmt/colony-tickets/${ticket.id}/notes`, payload)
      .then(({ data }) => onClosed(data))
      .catch((e) => {
        if (queueIfOffline(e, { portal: "api", method: "post", path: `/mgmt/colony-tickets/${ticket.id}/notes`, body: payload, label: `Close ticket "${ticket.title}"` })) return;
        onClosed(ticket); // restore — nothing was saved server-side
        toast.error(
          "Couldn't save the closing photos — the ticket was NOT closed. " +
          (formatErr(e.response?.data?.detail) || e.message)
        );
      });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} className="max-w-md" data-testid={`close-ticket-dialog-${ticket.id}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Close ticket
          </DialogTitle>
          <DialogDescription>
            Attach photos of the completed work for &quot;{ticket.title}&quot; — or skip and close without photos.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            {photos.map((p, i) => (
              <div key={i} className="relative">
                <LivePhotoSurface value={p} alt={`upload ${i + 1}`} className="w-14 h-14 rounded-sm border border-border" testId={`close-ticket-preview-${ticket.id}-${i}`} />
                <button
                  type="button"
                  onClick={() => setPhotos((prev) => prev.filter((_, x) => x !== i))}
                  className="absolute -top-1 -right-1 bg-background border border-border rounded-full w-5 h-5 flex items-center justify-center tap-exempt"
                  aria-label="Remove photo"
                  data-testid={`close-ticket-photo-remove-${ticket.id}-${i}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            {photos.length < 6 && (
              <PhotoAddButtons onFiles={addFiles} testidPrefix={`close-ticket-${ticket.id}`} />
            )}
          </div>
          <Textarea
            placeholder="Optional closing note…  (you can also paste a picture here)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onPaste={pasteToFiles(addFiles)}
            rows={2}
            className="rounded-sm"
            data-testid={`close-ticket-note-${ticket.id}`}
          />
        </div>
        <DialogFooter className="gap-2 sm:gap-2 flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            className="rounded-sm"
            onClick={() => onOpenChange(false)}
           
            data-testid={`close-ticket-cancel-${ticket.id}`}
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm"
            onClick={onSkip}
           
            data-testid={`close-ticket-skip-${ticket.id}`}
          >
            Skip & close
          </Button>
          <Button
            size="sm"
            className="rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white"
            onClick={closeWithPhotos}
           
            data-testid={`close-ticket-with-photos-${ticket.id}`}
          >
            <CheckCircle2 className="w-3.5 h-3.5 mr-2" /> {"Close with photos"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



function AdminNewTicketDialog({ open, onOpenChange, colonies, eligible = [], defaultColonyId = "", onCreated }) {
  const [colonyId, setColonyId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [photos, setPhotos] = useState([]);
  const [scheduledFor, setScheduledFor] = useState("");
  const [dateOpen, setDateOpen] = useState(false); // optional per spec — collapsed by default
  const [notify, setNotify] = useState(true);
  const [urgent, setUrgent] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const initializedForOpenRef = useRef(false);

  useEffect(() => {
    if (!open) {
      initializedForOpenRef.current = false;
      return;
    }
    if (initializedForOpenRef.current) return;
    initializedForOpenRef.current = true;
    setColonyId(defaultColonyId || colonies[0]?.id || "");
    setTitle("");
    setDescription("");
    setPhotos([]);
    setScheduledFor("");
    setDateOpen(false);
    setNotify(true);
    setUrgent(false);
    setAssignees([]);
    setAssignOpen(false);
  }, [open, defaultColonyId, colonies]);

  const addFiles = async (files) => {
    if (!files) return;
    try {
      const result = await processMediaFiles(files, { max: 6, existingCount: photos.length, client: api, scope: "mgmt_usa_nj", colonyId });
      setPhotos((current) => [...current, ...result.items].slice(0, 6));
      if (result.ignoredVideos) toast.message("Select the MOV together with its matching iPhone photo.");
    } catch (error) { toast.error(error.message || "Could not add that photo."); }
  };

  const submit = async () => {
    if (!colonyId) return toast.error("Pick which colony this ticket belongs to.");
    if (!title.trim()) return toast.error("Give the ticket a short title.");
    setSaving(true);
    const body = {
      colony_id: colonyId,
      title: title.trim(),
      description: description.trim(),
      photos,
      scheduled_for: scheduledFor || null,
      notify_colony: notify,
      urgent,
      ...(assignees.length ? { assignees } : {}),
    };
    try {
      const { data } = await api.post("/mgmt/colony-tickets", body);
      onCreated(data);
    } catch (e) {
      if (queueIfOffline(e, { portal: "api", method: "post", path: "/mgmt/colony-tickets", body, label: `Create ticket "${body.title}"` })) {
        onOpenChange(false);
      } else {
        toast.error(formatErr(e.response?.data?.detail) || e.message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="mgmt-new-ticket-dialog">
        <DialogHeader>
          <DialogTitle>New ticket</DialogTitle>
          <DialogDescription>
            Log a job for a bungalow colony. Optionally attach photos and a revisit date.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Colony *</Label>
            <Select value={colonyId} onValueChange={setColonyId}>
              <SelectTrigger className="rounded-sm h-10" data-testid="mgmt-new-ticket-colony">
                <SelectValue placeholder="Choose colony" />
              </SelectTrigger>
              <SelectContent>
                {colonies.map((c) => (
                  <SelectItem key={c.id} value={c.id}><ColonyDotName id={c.id} name={c.name} /></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Title *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Broken heater at unit 12"
              className="rounded-sm h-10"
              data-testid="mgmt-new-ticket-title"
              maxLength={120}
            />
          </div>
          <div>
            <Label>Details</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onPaste={pasteToFiles(addFiles)}
              rows={3}
              placeholder="What needs doing, where on the property, any context  (you can also paste a picture here)"
              className="rounded-sm"
              data-testid="mgmt-new-ticket-description"
            />
          </div>
          <label className="flex items-center gap-2 border border-red-200 bg-red-50/60 rounded-sm px-3 py-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={urgent}
              onChange={(e) => setUrgent(e.target.checked)}
              className="w-4 h-4 accent-red-600"
              data-testid="mgmt-new-ticket-urgent"
            />
            <span className="text-sm font-semibold text-red-700">Mark as urgent</span>
            <span className="text-xs text-red-600/70 ml-auto">Shows red &amp; jumps to the top</span>
          </label>
          {!assignOpen ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="rounded-sm px-2 text-muted-foreground"
              onClick={() => setAssignOpen(true)}
              data-testid="mgmt-new-ticket-assign-toggle"
            >
              <UserPlus className="w-3.5 h-3.5 mr-2" /> Assign to specific people (optional)
            </Button>
          ) : (
            <div>
              <div className="flex items-center justify-between">
                <Label>Assign to</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="rounded-sm px-2 text-muted-foreground h-7"
                  onClick={() => { setAssignees([]); setAssignOpen(false); }}
                  data-testid="mgmt-new-ticket-assign-clear"
                >
                  <X className="w-3.5 h-3.5 mr-1" /> Everyone instead
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mb-1">Only the people you pick will see this ticket and get emailed.</p>
              {eligible.length === 0 ? (
                <p className="text-xs text-muted-foreground border border-dashed border-border rounded-sm px-3 py-2">No worker logins yet.</p>
              ) : (
                <div className="border border-border rounded-sm max-h-36 overflow-y-auto" data-testid="mgmt-new-ticket-assignees">
                  {eligible.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 text-sm border-b border-border last:border-b-0 cursor-pointer hover:bg-secondary/40">
                      <input
                        type="checkbox"
                        checked={assignees.includes(u.id)}
                        onChange={() => setAssignees((prev) => prev.includes(u.id) ? prev.filter((x) => x !== u.id) : [...prev, u.id])}
                        data-testid={`mgmt-new-ticket-assignee-${u.id}`}
                      />
                      {u.name || u.email}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          <div>
            <Label>Photos (up to 6)</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {photos.map((p, i) => (
                <div key={i} className="relative">
                  <LivePhotoSurface value={p} alt={`upload ${i + 1}`} className="w-14 h-14 rounded-sm border border-border" testId={`mgmt-new-ticket-preview-${i}`} />
                  <button
                    type="button"
                    onClick={() => setPhotos((prev) => prev.filter((_, j) => j !== i))}
                    className="absolute -top-1 -right-1 bg-background border border-border rounded-full w-5 h-5 flex items-center justify-center tap-exempt"
                    aria-label="Remove photo"
                    data-testid={`mgmt-new-ticket-photo-remove-${i}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {photos.length < 6 && (
                <PhotoAddButtons onFiles={addFiles} testidPrefix="mgmt-new-ticket" />
              )}
            </div>
          </div>
          {/* Optional revisit-date — collapsed by default (spec: opt-in, no default value). */}
          {!dateOpen ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="rounded-sm px-2 text-muted-foreground"
              onClick={() => setDateOpen(true)}
              data-testid="mgmt-new-ticket-schedule-toggle"
            >
              <CalendarClock className="w-3.5 h-3.5 mr-2" /> Schedule for later (optional)
            </Button>
          ) : (
            <div className="flex items-end gap-2 flex-wrap">
              <div>
                <Label>Date &amp; time</Label>
                <Input
                  type="datetime-local"
                  value={scheduledFor}
                  onChange={(e) => setScheduledFor(e.target.value)}
                  className="rounded-sm h-10 w-56"
                  data-testid="mgmt-new-ticket-schedule-input"
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="rounded-sm text-muted-foreground"
                onClick={() => { setScheduledFor(""); setDateOpen(false); }}
              >
                Remove
              </Button>
              <p className="text-xs text-muted-foreground w-full">
                Pick a future date &amp; time and the ticket stays &quot;Scheduled&quot; (visible to you and the colony, hidden from workers) until it goes live.
              </p>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
              data-testid="mgmt-new-ticket-notify"
            />
            Email the colony contacts about this new ticket
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="mgmt-new-ticket-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving || !colonyId || !title.trim()}
            className="rounded-sm"
            data-testid="mgmt-new-ticket-submit"
          >
            {saving ? "Creating…" : "Create ticket"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

